import { useState, useEffect } from 'react';
import { numericProps } from '../utils/numericInput';
import { SPORTS, selectableSports, legacySportKey, isGrapplingSport, isStrikingSport, isCombatSport, isCardioSport, isStrengthSport, sportCategory } from '../data/sports';
import WeightForm from './workouts/WeightForm';
import GrapplingForm from './workouts/GrapplingForm';
import StrikingForm from './workouts/StrikingForm';
import MMAForm from './workouts/MMAForm';
import CardioForm from './workouts/CardioForm';
import { BJJ_BELTS, beltLabel } from './BjjPromotionSummary';
import DraftResumePrompt from './DraftResumePrompt';
import { clearRecordDraft, getRecordDraft, RECORD_DRAFT_RESUME_KEY, saveRecordDraft } from '../utils/recordDraft';
// RecordDetail 등 기존 코드의 import 경로를 유지하기 위해 re-export
export { default as BodyDiagram } from './workouts/shared/BodyDiagram';

// ─────────────────────────────────────────────────────────────
// 공통 UI 컴포넌트
function FormCard({ title, desc, children }) {
    return (
        <section className="record-form-card">
            <div className="record-form-card-head">
                <div>
                    <div className="record-form-card-title">{title}</div>
                    {desc && <div className="record-form-card-desc">{desc}</div>}
                </div>
            </div>
            {children}
        </section>
    );
}

function CondPips({ value, onChange, filled = true }) {
    return (
        <div className="cond-row">
            {[1, 2, 3, 4, 5].map(i => (
                <div
                    key={i}
                    className={`cond-pip ${i <= value ? 'filled' : ''}`}
                    style={i <= value && !filled ? { background: 'var(--text3)' } : {}}
                    onClick={() => onChange(i)}
                />
            ))}
        </div>
    );
}

function formatTimer(sec) {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (h) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ─────────────────────────────────────────────────────────────
// 완료 화면
// ─────────────────────────────────────────────────────────────
function normalizeDate(dateStr) {
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    return d;
}

function calcStreakFrom(records, startDate, sport = null) {
    if (!startDate) return 0;
    const days = new Set(records
        .filter(r => !sport || r.sport === sport)
        .map(r => r.date));
    const d = normalizeDate(startDate);
    let streak = 0;
    while (days.has(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`)) {
        streak += 1;
        d.setDate(d.getDate() - 1);
    }
    return streak;
}

function CompletionScreen({ record, records, onViewRecords }) {
    const sport = SPORTS[record.sport];
    const allStreak = calcStreakFrom(records, record.date);
    const sportStreak = calcStreakFrom(records, record.date, record.sport);
    const sportTotal = records.filter(r => r.sport === record.sport).length;
    const sameDayCount = records.filter(r => r.date === record.date).length;

    return (
        <div className="complete-screen">
            <div className="complete-check">
                <svg viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="28" />
                    <path d="M19 33.5l8.2 8.2L46 22.5" />
                </svg>
            </div>
            <div className="complete-title">운동 기록 완료</div>
            <div className="complete-sub">
                {sport?.name || record.sport} · {record.duration || 0}분
                {record.buddies?.length > 0 && ` · ${record.buddies.length}명과 함께`}
            </div>
            <div className="complete-stats">
                <div className="complete-stat"><span>{allStreak}</span><label>전체 연속 운동일</label></div>
                <div className="complete-stat"><span>{sportStreak}</span><label>{sport?.name || record.sport} 연속일</label></div>
                <div className="complete-stat"><span>{sportTotal}</span><label>이 종목 누적 기록</label></div>
                <div className="complete-stat"><span>{sameDayCount}</span><label>이날 총 기록</label></div>
            </div>
            <button className="save-btn" onClick={onViewRecords}>기록 보러가기</button>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// 초기 폼 상태
// ─────────────────────────────────────────────────────────────
const INIT_FORM = {
    sport: null,
    exercises: [{ name: '', muscleGroup: '', sets: [{ reps: '', weight: '' }] }],
    techniques: [{ name: '', position: '', point: '', result: '', category: '' }],
    buddies: [],
    sparring: false,
    condScore: 3, fatScore: 3,
    focus: '',
    drillTime: '',
    strikingDrills: [],
    sparringRoundsList: [],
    visibility: localStorage.getItem('wj_visibility') || 'public',
    postMessage: '',
    _recordId: '',
    _postId: null,
};

const TEMPLATES_PER_PAGE = 5;

const INIT_PROMOTION = {
    date: '',
    place: '',
    belt: 'blue',
    stripe: 0,
    instructor: '',
    visibility: 'public',
    message: '',
};

const hydrateFormFromDraft = (draft) => {
    const data = draft?.formData || {};
    const cardio = data.cardioValues || {};
    return {
        ...INIT_FORM,
        ...(draft?.form || {}),
        _date: data.date || draft?.form?._date || '',
        _duration: data.duration || draft?.form?._duration || '',
        _diary: data.diary || draft?.form?._diary || '',
        _sparRounds: data.sparRounds || draft?.form?._sparRounds || '',
        _sparMin: data.sparMin || draft?.form?._sparMin || '',
        _dist: cardio.dist || draft?.form?._dist || '',
        _cal: cardio.cal || draft?.form?._cal || '',
        _avgHR: cardio.avgHR || draft?.form?._avgHR || '',
        _pace: cardio.pace || draft?.form?._pace || '',
        _power: cardio.power || draft?.form?._power || '',
        _rpm: cardio.rpm || draft?.form?._rpm || '',
        _speed: cardio.speed || draft?.form?._speed || '',
        _elevation: cardio.elevation || draft?.form?._elevation || '',
        _floors: cardio.floors || draft?.form?._floors || '',
        _stepRate: cardio.stepRate || draft?.form?._stepRate || '',
        _level: cardio.level || draft?.form?._level || '',
        _intervalWork: cardio.intervalWork || draft?.form?._intervalWork || '',
        _intervalRest: cardio.intervalRest || draft?.form?._intervalRest || '',
        _intervalSets: cardio.intervalSets || draft?.form?._intervalSets || '',
    };
};

// ─────────────────────────────────────────────────────────────
// 메인 컴포넌트
// ─────────────────────────────────────────────────────────────
export default function AddRecord({ records, setRecords, weights, setWeights, templates, setTemplates, promotions = [], setPromotions, friends, showToast, setTab, pendingEdit, setPendingEdit, pendingDate, setPendingDate, createPost, createPromotionPost, updatePost, deletePostForRecord }) {
    const d0 = new Date();
    const today = `${d0.getFullYear()}-${String(d0.getMonth() + 1).padStart(2, '0')}-${String(d0.getDate()).padStart(2, '0')}`;
    const [form, setForm] = useState(INIT_FORM);
    const [weightInput, setWeightInput] = useState('');
    const [weightDate, setWeightDate] = useState(today);
    const [tplPage, setTplPage] = useState(0);
    const [confirmTplIdx, setConfirmTplIdx] = useState(null);
    const [completion, setCompletion] = useState(null);
    const [sportQuery, setSportQuery] = useState('');
    const [editIdx, setEditIdx] = useState(null);
    const [step, setStep] = useState('hub');
    const [promotion, setPromotion] = useState({ ...INIT_PROMOTION, date: today });
    const [formDraftData, setFormDraftData] = useState(null);

    const latestWeight = weights ? [...weights].sort((a, b) => new Date(b.date) - new Date(a.date))[0] : null;
    const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
    const setPromo = (key, val) => setPromotion(p => ({ ...p, [key]: val }));

    const resumeDraft = () => {
        const draft = getRecordDraft();
        if (!draft) {
            showToast?.('이어 쓸 임시 저장본이 없어요');
            return;
        }
        setForm(hydrateFormFromDraft(draft));
        setFormDraftData(draft.formData || null);
        setEditIdx(null);
        setCompletion(null);
        setSportQuery('');
        setStep('form');
    };

    const resetDraftableForm = () => {
        setFormDraftData(null);
        setForm(INIT_FORM);
    };

    useEffect(() => {
        if (pendingEdit !== null) { startEdit(pendingEdit); setPendingEdit(null); }
    }, [pendingEdit]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (sessionStorage.getItem(RECORD_DRAFT_RESUME_KEY) !== '1') return;
        sessionStorage.removeItem(RECORD_DRAFT_RESUME_KEY);
        resumeDraft();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (step !== 'form' || editIdx !== null || !form.sport) return;
        saveRecordDraft({ form, formData: formDraftData || {} });
    }, [form, formDraftData, step, editIdx]);

    useEffect(() => {
        if (!pendingDate) return;
        setForm(f => ({ ...f, _date: pendingDate }));
        setStep('hub');
        setPendingDate(null);
    }, [pendingDate, setPendingDate]);

    const addWeight = () => {
        const v = weightInput.trim();
        if (!v || isNaN(+v)) { showToast('체중을 입력해주세요'); return; }
        const targetDate = weightDate || today;
        const existing = weights.findIndex(w => w.date === targetDate);
        const next = [...weights];
        if (existing >= 0) next[existing] = { date: targetDate, val: v };
        else next.push({ date: targetDate, val: v });
        setWeights(next);
        setWeightInput('');
        showToast(`${v}kg 기록 완료!`);
    };

    const savePromotion = () => {
        if (!promotion.date || !promotion.place.trim() || !promotion.belt || !promotion.instructor.trim()) {
            showToast('승급 정보를 모두 입력해주세요');
            return;
        }
        const item = {
            ...promotion,
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            place: promotion.place.trim(),
            instructor: promotion.instructor.trim(),
            message: promotion.message.trim(),
            stripe: +promotion.stripe || 0,
        };
        if (createPromotionPost) {
            createPromotionPost(item, item.visibility)
                .then(postId => setPromotions([{ ...item, postId: postId || null }, ...(promotions || [])]))
                .catch(() => {
                    setPromotions([item, ...(promotions || [])]);
                    showToast('승급 기록은 저장됐지만 피드 공개에 실패했어요');
                });
        } else {
            setPromotions([item, ...(promotions || [])]);
        }
        showToast('승급 기록 저장 완료!');
        setPromotion({ ...INIT_PROMOTION, date: today });
        setTab('goals');
    };

    const normalizeTechniques = (raw) => {
        if (!raw) return [{ name: '', position: '', point: '', result: '', category: '' }];
        if (Array.isArray(raw)) return raw.map(t =>
            typeof t === 'string'
                ? { name: t, position: '', point: '', result: '', category: '' }
                : { position: '', result: '', category: '', ...t });
        return [{ name: raw, position: '', point: '', result: '', category: '' }];
    };

    const applyTemplate = (t) => {
        setFormDraftData(null);
        setForm({
            ...INIT_FORM,
            sport: t.sport,
            focus: t.focus || '',
            sparring: t.sparring || false,
            exercises: (t.exercises?.length
                ? t.exercises
                : [{ name: t.exerciseName || '', muscleGroup: t.muscleGroup || '', sets: t.sets || [] }]
            ).map(ex => ({ name: ex.name || '', muscleGroup: ex.muscleGroup || '', sets: ex.sets?.map(s => ({ ...s })) || [{ reps: '', weight: '' }] })),
            techniques: [{ name: '', position: '', point: '', result: '', category: '' }],
            _duration: t.duration || '',
            _date: form._date,
        });
        setStep('form');
    };

    const startEdit = (idx) => {
        const r = records[idx];
        setFormDraftData(null);
        setForm({
            sport: r.sport,
            sparring: r.sparring || false,
            buddies: r.buddies || [],
            condScore: r.condScore || 3, fatScore: r.fatScore || 3,
            focus: r.focus || '',
            exercises: r.exercises?.length
                ? r.exercises.map(ex => ({ name: ex.name || '', muscleGroup: ex.muscleGroup || r.muscleGroup || '', sets: ex.sets?.map(s => ({ ...s })) || [{ reps: '', weight: '' }] }))
                : [{ name: r.exerciseName || '', muscleGroup: r.muscleGroup || '', sets: r.sets?.map(s => ({ ...s })) || [{ reps: '', weight: '' }] }],
            techniques: normalizeTechniques(r.techniques),
            sparringRoundsList: r.sparringRoundsList || [],
            drillTime: r.drillTime || '',
            strikingDrills: r.strikingDrills || [],
            visibility: r.visibility || localStorage.getItem('wj_visibility') || 'friends',
            postMessage: r.postMessage || '',
            _recordId: r.recordId || '',
            _postId: r.postId || null,
            _date: r.date, _duration: r.duration, _diary: r.diary,
            _sparRounds: r.sparringRounds, _sparMin: r.sparringMin,
            // cardio fields
            _dist: r.distance, _cal: r.calories,
            _avgHR: r.avgHR || '', _pace: r.pace || '', _power: r.power || '',
            _rpm: r.rpm || '', _speed: r.speed || '', _elevation: r.elevation || '',
            _floors: r.floors || '', _stepRate: r.stepRate || '', _level: r.level || '',
            _intervalWork: r.intervalWork || '', _intervalRest: r.intervalRest || '', _intervalSets: r.intervalSets || '',
        });
        setEditIdx(idx);
        setStep('form');
    };

    const buildRecord = (formData, cardioValues) => {
        const rec = {
            recordId: form._recordId || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            sport: form.sport,
            date: formData.date,
            duration: formData.duration,
            condScore: form.condScore,
            fatScore: form.fatScore,
            diary: formData.diary,
            focus: (isCardioSport(form.sport) ? cardioValues?.focus : form.focus) || '',
            buddies: form.buddies || [],
            techniques: form.techniques.filter(t => t.name || t.point),
            visibility: form.visibility || 'friends',
            postMessage: form.postMessage || '',
            postId: form._postId || null,
        };

        if (isCombatSport(form.sport)) {
            Object.assign(rec, {
                sparring: form.sparring,
                sparringRounds: formData.sparRounds,
                sparringMin: formData.sparMin,
                sparringRoundsList: form.sparringRoundsList || [],
            });
            if (isGrapplingSport(form.sport)) {
                rec.gi = SPORTS[form.sport]?.name;
                rec.drillTime = form.drillTime || '';
            }
            if (isStrikingSport(form.sport)) {
                rec.strType = SPORTS[form.sport]?.name;
                rec.strikingDrills = form.strikingDrills || [];
            }
            if (sportCategory(form.sport) === 'combat') {
                rec.strikingDrills = form.strikingDrills || [];
            }
        }

        if (isStrengthSport(form.sport)) {
            const exList = form.exercises
                .map(ex => ({ name: ex.name, muscleGroup: ex.muscleGroup || '', sets: ex.sets.filter(s => s.reps || s.weight) }))
                .filter(ex => ex.name || ex.sets.length);
            Object.assign(rec, {
                exercises: exList,
                muscleGroups: [...new Set(exList.map(ex => ex.muscleGroup).filter(Boolean))],
            });
        }

        if (isCardioSport(form.sport)) {
            const cv = cardioValues || {};
            const extras = {};
            const optionals = ['avgHR', 'pace', 'power', 'rpm', 'speed', 'elevation', 'floors', 'stepRate', 'level', 'intervalWork', 'intervalRest', 'intervalSets'];
            optionals.forEach(k => { if (cv[k]) extras[k] = cv[k]; });
            Object.assign(rec, {
                cardioType: SPORTS[form.sport]?.name,
                distance: cv.dist,
                calories: cv.cal,
                ...extras,
            });
        }

        return rec;
    };

    const buildTemplate = (formData, name) => ({
        name,
        sport: form.sport,
        focus: form.focus,
        sparring: form.sparring,
        exercises: isStrengthSport(form.sport)
            ? form.exercises.map(ex => ({ name: ex.name, muscleGroup: ex.muscleGroup || '', sets: ex.sets.map(s => ({ ...s })) }))
            : undefined,
        duration: formData.duration,
    });

    const saveAsTemplate = (formData, name) => {
        setTemplates([...templates, buildTemplate(formData, name)]);
        showToast('루틴 저장!');
    };

    const deleteTemplate = (i) => {
        const next = [...templates]; next.splice(i, 1);
        setTemplates(next);
        showToast('삭제했어요');
        setConfirmTplIdx(null);
        if (tplPage > 0 && tplPage * TEMPLATES_PER_PAGE >= next.length) setTplPage(p => p - 1);
    };

    const handleSave = (formData, cardioValues, tplName = null) => {
        const rec = buildRecord(formData, cardioValues);
        if (tplName) saveAsTemplate(formData, tplName);

        if (editIdx !== null) {
            const next = [...records]; next[editIdx] = rec; setRecords(next);
            if (rec.visibility === 'private') {
                if (deletePostForRecord) deletePostForRecord(rec).catch(console.error);
            } else if (rec.postId && updatePost) {
                updatePost(rec.postId, rec, rec.visibility).catch(console.error);
            } else if (createPost) {
                createPost(rec, rec.visibility).then(postId => {
                    if (postId) { const u = [...records]; u[editIdx] = { ...rec, postId }; setRecords(u); }
                }).catch(console.error);
            }
            showToast(tplName ? '수정 저장 + 루틴 저장!' : '수정 완료!');
            setTab('cal');
        } else {
            setRecords([rec, ...records]);
            if (createPost && rec.visibility !== 'private') {
                createPost(rec, rec.visibility).then(postId => {
                    if (postId) setRecords([{ ...rec, postId }, ...records]);
                }).catch(() => showToast('운동은 저장됐지만 소셜 공개에 실패했어요'));
            }
            showToast(tplName ? '기록 저장 + 루틴 저장!' : '기록 저장 완료!');
            setCompletion({ record: rec, records: [rec, ...records] });
        }

        setEditIdx(null);
        resetDraftableForm();
        clearRecordDraft();
        setStep('hub');
    };

    const draftPrompt = step !== 'form' && editIdx === null
        ? <DraftResumePrompt onResume={resumeDraft} showToast={showToast} />
        : null;

    // ── 완료 화면 ──────────────────────────────────────────
    if (completion) {
        return (
            <CompletionScreen
                record={completion.record}
                records={completion.records}
                onViewRecords={() => { setCompletion(null); setTab('cal'); }}
            />
        );
    }

    // ── 템플릿 목록 ────────────────────────────────────────
    const renderTemplateList = () => {
        const totalPages = Math.ceil(templates.length / TEMPLATES_PER_PAGE);
        const pageTpls = templates.slice(tplPage * TEMPLATES_PER_PAGE, (tplPage + 1) * TEMPLATES_PER_PAGE);
        return (
            <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                    <p className="section-label" style={{ margin: 0 }}>루틴 템플릿</p>
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text3)' }}>
                            <button className="tpl-btn" style={{ padding: '4px 10px', opacity: tplPage === 0 ? 0.3 : 1 }} disabled={tplPage === 0} onClick={() => setTplPage(p => p - 1)}>‹</button>
                            <span>{tplPage + 1} / {totalPages}</span>
                            <button className="tpl-btn" style={{ padding: '4px 10px', opacity: tplPage === totalPages - 1 ? 0.3 : 1 }} disabled={tplPage === totalPages - 1} onClick={() => setTplPage(p => p + 1)}>›</button>
                        </div>
                    )}
                </div>
                {templates.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-text">아직 저장된 루틴이 없어요.<br />운동 기록을 추가하면서 루틴을 저장해 보세요.</div>
                    </div>
                ) : (
                    <div style={{ marginTop: 8 }}>
                        {pageTpls.map((t, i) => {
                            const realIdx = tplPage * TEMPLATES_PER_PAGE + i;
                            const isStr = isStrengthSport(t.sport);
                            return (
                                <div key={realIdx} className="template-card">
                                    <div className="template-name">
                                        <span>{t.name}</span>
                                        <div className="sport-dot" style={{ background: SPORTS[t.sport]?.color || '#888', width: 8, height: 8 }} />
                                    </div>
                                    <div className="template-tags">
                                        <span className="tag">{SPORTS[t.sport]?.name || t.sport}</span>
                                        {t.duration && <span className="tag">{t.duration}분</span>}
                                        {isStr && t.exercises?.length > 0 && [...new Set(t.exercises.map(ex => ex.muscleGroup).filter(Boolean))].map(m => <span key={m} className="tag">{m}</span>)}
                                        {t.sparring && <span className="tag">스파링</span>}
                                        {isStr && t.exercises?.length > 0 && <span className="tag">{t.exercises.length}개 운동</span>}
                                    </div>
                                    <div className="template-actions">
                                        <button className="tpl-btn primary" onClick={() => applyTemplate(t)}>이 루틴으로 기록</button>
                                        <button className="tpl-btn del" onClick={() => setConfirmTplIdx(realIdx)}>삭제</button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                {confirmTplIdx !== null && (
                    <div className="confirm-backdrop">
                        <div className="confirm-panel">
                            <div className="confirm-title">루틴을 삭제할까요?</div>
                            <div className="confirm-desc">{templates[confirmTplIdx]?.name || '선택한 루틴'}은 다시 불러올 수 없어요.</div>
                            <div className="confirm-actions">
                                <button className="action-btn" onClick={() => setConfirmTplIdx(null)}>취소</button>
                                <button className="action-btn danger" onClick={() => deleteTemplate(confirmTplIdx)}>삭제</button>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    };

    // ── 허브 ──────────────────────────────────────────────
    if (step === 'hub') {
        return (
            <div className="add-hub">
                <button className="add-hub-btn" onClick={() => setStep('routine')}>
                    <span className="add-hub-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                            <path d="M6 4h12v5H6z" /><path d="M6 13h12v7H6z" />
                        </svg>
                    </span>
                    <span><strong>루틴 운동 기록 추가하기</strong><em>저장해둔 루틴으로 빠르게 시작</em></span>
                </button>
                <button className="add-hub-btn" onClick={() => setStep('newSport')}>
                    <span className="add-hub-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                            <path d="M12 5v14" /><path d="M5 12h14" />
                        </svg>
                    </span>
                    <span><strong>새 운동 기록 추가하기</strong><em>종목을 검색해서 새 세션 작성</em></span>
                </button>
                <button className="add-hub-btn" onClick={() => setStep('weight')}>
                    <span className="add-hub-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                            <path d="M7 8h10l1 12H6L7 8z" /><path d="M9 8a3 3 0 016 0" />
                        </svg>
                    </span>
                    <span><strong>체중 추가하기</strong><em>{latestWeight ? `최근 ${latestWeight.val}kg · ${latestWeight.date}` : '오늘 체중만 간단히 기록'}</em></span>
                </button>
                <button className="add-hub-btn" onClick={() => setStep('qualification')}>
                    <span className="add-hub-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                            <path d="M5 7h14v4H5z" /><path d="M7 11v8h10v-8" /><path d="M9 15h6" />
                        </svg>
                    </span>
                    <span><strong>자격 기록 추가하기</strong><em>주짓수 승급, 유도 승단 등을 기록해 보세요</em></span>
                </button>
                {draftPrompt}
            </div>
        );
    }

    if (step === 'qualification') {
        return (
            <>
                <button className="back-btn" onClick={() => setStep('hub')}>← 추가 방식 선택</button>
                <div className="add-hub">
                    <button className="add-hub-btn" onClick={() => setStep('promotion')}>
                        <span className="add-hub-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                                <path d="M5 7h14v4H5z" /><path d="M7 11v8h10v-8" /><path d="M9 15h6" />
                            </svg>
                        </span>
                        <span><strong>주짓수 승급 기록 추가하기</strong><em>벨트와 그랄, 승급 소감을 피드에 공유</em></span>
                    </button>
                    {draftPrompt}
                </div>
            </>
        );
    }

    if (step === 'promotion') {
        return (
            <>
                <button className="back-btn" onClick={() => setStep('hub')}>← 추가 방식 선택</button>
                <FormCard title="주짓수 승급 기록" desc="언제, 어디서, 누구에게 어떤 자격으로 승급했는지 남겨요.">
                    <div className="form-group">
                        <label className="form-label">승급일</label>
                        <input className="form-input" type="date" value={promotion.date} onChange={e => setPromo('date', e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label className="form-label">승급 장소</label>
                        <input className="form-input" placeholder="체육관 이름 등" value={promotion.place} onChange={e => setPromo('place', e.target.value)} />
                    </div>
                    <div className="metric-grid">
                        <div className="form-group">
                            <label className="form-label">벨트</label>
                            <select className="form-input" value={promotion.belt} onChange={e => setPromo('belt', e.target.value)}>
                                {BJJ_BELTS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label">그랄</label>
                            <select className="form-input" value={promotion.stripe} onChange={e => setPromo('stripe', e.target.value)}>
                                {[0, 1, 2, 3, 4].map(n => <option key={n} value={n}>{n}그랄</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">승급 지도자</label>
                        <input className="form-input" placeholder="예: 홍길동 관장님" value={promotion.instructor} onChange={e => setPromo('instructor', e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label className="form-label">피드 공개 범위</label>
                        <div className="visibility-card-grid">
                            {[
                                { key: 'private', title: '내 피드에만', desc: '나만 볼 수 있는 피드 글로 저장' },
                                { key: 'friends', title: '친구 공개', desc: '나와 친구 피드에 표시' },
                                { key: 'public', title: '전체 공개', desc: '모든 사용자 피드에 표시' },
                            ].map(option => (
                                <button key={option.key} className={`visibility-card ${promotion.visibility === option.key ? 'active' : ''}`} onClick={() => setPromo('visibility', option.key)}>
                                    <strong>{option.title}</strong>
                                    <span>{option.desc}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">승급 소감</label>
                        <textarea className="form-input" rows={4} placeholder={`${beltLabel(promotion.belt)} ${promotion.stripe}그랄 승급 소감을 남겨보세요.`} value={promotion.message} onChange={e => setPromo('message', e.target.value)} />
                    </div>
                    <button className="save-btn" onClick={savePromotion}>승급 기록 저장하기</button>
                </FormCard>
            </>
        );
    }

    // ── 체중 ──────────────────────────────────────────────
    if (step === 'weight') {
        return (
            <>
                <button className="back-btn" onClick={() => setStep('hub')}>← 추가 방식 선택</button>
                <FormCard title="체중 기록" desc="원하는 날짜의 체중만 빠르게 저장해요. 같은 날짜 기록은 새 값으로 바뀝니다.">
                    <div className="form-group">
                        <label className="form-label">날짜</label>
                        <input className="form-input" type="date" value={weightDate} onChange={e => setWeightDate(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label className="form-label">체중</label>
                        <div className="weight-row">
                            <div className="weight-input-wrap">
                                <input className="form-input"
                                    placeholder={latestWeight ? latestWeight.val : '70.0'}
                                    onKeyDown={e => e.key === 'Enter' && addWeight()}
                                    {...numericProps(weightInput, setWeightInput, showToast, { allowDecimal: true })} />
                            </div>
                            <button className="tpl-btn" style={{ flex: 0, padding: '10px 16px', whiteSpace: 'nowrap' }} onClick={addWeight}>저장</button>
                        </div>
                    </div>
                    {latestWeight && (
                        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                            최근: {latestWeight.val}kg ({latestWeight.date})
                        </div>
                    )}
                </FormCard>
                {draftPrompt}
            </>
        );
    }

    // ── 루틴 목록 ─────────────────────────────────────────
    if (step === 'routine') {
        return (
            <>
                <button className="back-btn" onClick={() => setStep('hub')}>← 추가 방식 선택</button>
                {renderTemplateList()}
                {draftPrompt}
            </>
        );
    }

    // ── 종목 선택 ─────────────────────────────────────────
    if (step === 'newSport') {
        const q = sportQuery.trim().toLowerCase();
        const sports = selectableSports();
        const filteredSports = q ? sports.filter(([, v]) => `${v.name} ${v.sub}`.toLowerCase().includes(q)) : [];
        const recent = [];
        for (const r of [...records].sort((a, b) => new Date(b.date) - new Date(a.date))) {
            const k = legacySportKey(r);
            if (SPORTS[k] && !SPORTS[k].legacy && !recent.includes(k)) recent.push(k);
            if (recent.length === 6) break;
        }

        return (
            <>
                <button className="back-btn" onClick={() => setStep('hub')}>← 추가 방식 선택</button>
                {recent.length > 0 && (
                    <>
                        <p className="section-label">최근 기록한 종목</p>
                        <div className="recent-sport-grid">
                            {recent.map(k => (
                                <button key={k} className="sport-btn" onClick={() => { setFormDraftData(null); set('sport', k); setStep('form'); }}>
                                    <div className="sport-indicator" style={{ background: SPORTS[k].color }} />
                                    <div className="sport-btn-name">{SPORTS[k].name}</div>
                                    <div className="sport-btn-sub">{SPORTS[k].sub}</div>
                                </button>
                            ))}
                        </div>
                    </>
                )}
                <p className="section-label">{form._date ? `${form._date} 종목 검색` : '오늘의 종목 검색'}</p>
                <input
                    className="form-input"
                    placeholder="종목 검색 (예: 노기, 복싱, 사이클)"
                    value={sportQuery}
                    onChange={e => setSportQuery(e.target.value)}
                    style={{ marginBottom: 10 }}
                />
                <div className="sport-grid">
                    {filteredSports.map(([k, v]) => (
                        <button key={k} className="sport-btn" onClick={() => { setFormDraftData(null); set('sport', k); setStep('form'); }}>
                            <div className="sport-indicator" style={{ background: v.color }} />
                            <div className="sport-btn-name">{v.name}</div>
                            <div className="sport-btn-sub">{v.sub}</div>
                        </button>
                    ))}
                </div>
                {filteredSports.length === 0 && (
                    <div className="empty-state">
                        <div className="empty-text">
                            {q ? '검색 결과가 없어요.' : '종목명을 검색하거나 최근 기록한 종목을 선택해보세요.'}<br />
                            {q ? '다른 종목명으로 검색해보세요.' : '예: 노기, 복싱, 사이클'}
                        </div>
                    </div>
                )}
                {draftPrompt}
            </>
        );
    }

    // ── 폼 ────────────────────────────────────────────────
    const sp = SPORTS[form.sport];
    return (
        <FormStep
            form={{ ...form, _sportConfig: sp }}
            set={set}
            sp={sp}
            today={today}
            isEdit={editIdx !== null}
            onBack={() => { setStep('hub'); setEditIdx(null); resetDraftableForm(); }}
            onSave={handleSave}
            onDraftChange={setFormDraftData}
            showToast={showToast}
            records={records}
            friends={friends}
        />
    );
}

// ─────────────────────────────────────────────────────────────
// 폼 스텝
// ─────────────────────────────────────────────────────────────
function FormStep({ form, set, sp, today, isEdit, onBack, onSave, onDraftChange, showToast, records, friends }) {
    const np = (value, setter) => numericProps(value, setter, showToast);
    const [date, setDate] = useState(form._date || today);
    const initialDuration = +form._duration || 0;
    const [durationHours, setDurationHours] = useState(initialDuration ? String(Math.floor(initialDuration / 60)) : '');
    const [durationMinutes, setDurationMinutes] = useState(initialDuration ? String(initialDuration % 60) : '');
    const [diary, setDiary] = useState(form._diary || '');
    const [sparRounds, setSparRounds] = useState(form._sparRounds || '');
    const [sparMin, setSparMin] = useState(form._sparMin || '');
    const [cardioValues, setCardioValues] = useState({
        focus: form.focus || '',
        dist: form._dist || '', cal: form._cal || '', avgHR: form._avgHR || '',
        pace: form._pace || '', power: form._power || '', rpm: form._rpm || '',
        speed: form._speed || '', elevation: form._elevation || '',
        floors: form._floors || '', stepRate: form._stepRate || '', level: form._level || '',
        intervalWork: form._intervalWork || '', intervalRest: form._intervalRest || '', intervalSets: form._intervalSets || '',
    });
    const [showTplInput, setShowTplInput] = useState(false);
    const [tplName, setTplName] = useState('');
    const [showBuddyPicker, setShowBuddyPicker] = useState(false);
    const [buddyQuery, setBuddyQuery] = useState('');
    const [timerSeconds, setTimerSeconds] = useState(0);
    const [timerRunning, setTimerRunning] = useState(false);

    const isCombat = isCombatSport(form.sport);
    const isCardio = isCardioSport(form.sport);

    const sparringStats = (form.sparringRoundsList || []).reduce((acc, round) => {
        acc.count += +round.roundCount || 0;
        acc.minutes += (+round.roundCount || 0) * (+round.roundMinutes || 0);
        return acc;
    }, { count: 0, minutes: 0 });
    const hasTimedDetailedSparring = (form.sparringRoundsList || []).some(r => r.roundCount || r.roundMinutes);

    const validate = () => {
        const totalDuration = (+durationHours || 0) * 60 + (+durationMinutes || 0);
        if (!date) { showToast('날짜를 입력해주세요'); return false; }
        if (!totalDuration) { showToast('운동 시간을 입력해주세요'); return false; }
        return true;
    };

    const getFormData = () => ({
        date,
        duration: String((+durationHours || 0) * 60 + (+durationMinutes || 0)),
        diary,
        sparRounds: hasTimedDetailedSparring ? String(sparringStats.count || '') : sparRounds,
        sparMin: hasTimedDetailedSparring ? String(sparringStats.minutes || '') : sparMin,
    });

    useEffect(() => {
        onDraftChange?.({ ...getFormData(), cardioValues });
    }, [date, durationHours, durationMinutes, diary, sparRounds, sparMin, cardioValues]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!timerRunning) return undefined;
        const id = window.setInterval(() => setTimerSeconds(v => v + 1), 1000);
        return () => window.clearInterval(id);
    }, [timerRunning]);

    const toggleBuddy = (friend) => {
        const exists = (form.buddies || []).some(b => b.uid === friend.uid);
        if (exists) {
            set('buddies', (form.buddies || []).filter(b => b.uid !== friend.uid));
            set('sparringRoundsList', (form.sparringRoundsList || []).map(round =>
                round.opponent?.uid === friend.uid ? { ...round, opponent: null } : round
            ));
        } else {
            set('buddies', [...(form.buddies || []), { uid: friend.uid, userId: friend.userId, nickname: friend.nickname }]);
        }
    };

    const buddyCounts = records.reduce((acc, record) => {
        (record.buddies || []).forEach(b => { acc[b.uid] = (acc[b.uid] || 0) + 1; });
        return acc;
    }, {});
    const sortedFriends = [...(friends || [])]
        .filter(friend => {
            const q = buddyQuery.trim().toLowerCase();
            if (!q) return true;
            return friend.nickname?.toLowerCase().includes(q) || friend.userId?.toLowerCase().includes(q);
        })
        .sort((a, b) => (buddyCounts[b.uid] || 0) - (buddyCounts[a.uid] || 0) || a.nickname.localeCompare(b.nickname));

    // 카테고리에 맞는 스포츠 폼 렌더
    const renderSportForm = () => {
        const cat = sportCategory(form.sport);
        if (isCardio) {
            return (
                <CardioForm
                    sport={form.sport}
                    sportConfig={sp}
                    values={cardioValues}
                    onChange={setCardioValues}
                />
            );
        }
        if (isStrengthSport(form.sport)) {
            return <WeightForm form={form} set={set} records={records} />;
        }
        if (isGrapplingSport(form.sport)) {
            return (
                <GrapplingForm
                    form={form} set={set} friends={friends}
                    sparRounds={sparRounds} setSparRounds={setSparRounds}
                    sparMin={sparMin} setSparMin={setSparMin}
                />
            );
        }
        if (isStrikingSport(form.sport)) {
            return (
                <StrikingForm
                    form={form} set={set} friends={friends}
                    sparRounds={sparRounds} setSparRounds={setSparRounds}
                    sparMin={sparMin} setSparMin={setSparMin}
                />
            );
        }
        if (cat === 'combat') {
            return (
                <MMAForm
                    form={form} set={set} friends={friends}
                    sparRounds={sparRounds} setSparRounds={setSparRounds}
                    sparMin={sparMin} setSparMin={setSparMin}
                />
            );
        }
        return null;
    };

    return (
        <>
            <button className="back-btn" onClick={onBack}>← {isEdit ? '상세로' : '추가 방식 선택'}</button>

            <div className="record-form-hero">
                <div className="sport-dot" style={{ background: sp.color, width: 10, height: 10 }} />
                <div>
                    <div className="record-form-title">{sp.name}</div>
                    <div className="record-form-sub">{sp.formTitle || sp.name}</div>
                </div>
                {isEdit && <span className="editing-badge">수정 중</span>}
            </div>

            <FormCard title="세션 정보" desc="날짜와 총 운동 시간은 모든 기록의 기준이 됩니다.">
                <div className="form-group">
                    <label className="form-label">날짜 <span style={{ color: 'var(--coral)' }}>*</span></label>
                    <input className="form-input" type="date" value={date} onChange={e => setDate(e.target.value)} />
                </div>
                <div className="form-group">
                    <label className="form-label">운동 시간 <span style={{ color: 'var(--coral)' }}>*</span></label>
                    <div className="duration-inputs">
                        <div className="duration-input-wrap">
                            <input className="form-input" placeholder="1"
                                {...np(durationHours, setDurationHours)} />
                            <span>시간</span>
                        </div>
                        <div className="duration-input-wrap">
                            <input className="form-input" placeholder="30"
                                {...np(durationMinutes, setDurationMinutes)} />
                            <span>분</span>
                        </div>
                    </div>
                </div>
                {friends?.length > 0 && (
                    <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label">함께 운동한 친구</label>
                        <div className="buddy-summary">
                            {(form.buddies || []).length === 0 ? (
                                <span className="buddy-empty">선택된 친구 없음</span>
                            ) : (
                                <div className="buddy-tags">
                                    {(form.buddies || []).map(b => (
                                        <button key={b.uid} className="buddy-chip active" onClick={() => toggleBuddy(b)}>
                                            <span>{b.nickname?.slice(0, 1) || '?'}</span>
                                            {b.nickname}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <button className="tpl-btn primary buddy-add-btn" onClick={() => setShowBuddyPicker(true)}>친구 추가</button>
                        </div>
                    </div>
                )}
            </FormCard>

            <FormCard title="운동 타이머" desc="운동 중 앱을 켜두고 세션 시간을 가볍게 확인해요.">
                <div className="workout-timer">
                    <div className="workout-timer-time">{formatTimer(timerSeconds)}</div>
                    <div className="workout-timer-actions">
                        <button className="tpl-btn primary" onClick={() => setTimerRunning(v => !v)}>
                            {timerRunning ? '일시정지' : '시작'}
                        </button>
                        <button className="tpl-btn" onClick={() => { setTimerRunning(false); setTimerSeconds(0); }}>초기화</button>
                    </div>
                </div>
            </FormCard>

            <FormCard title={sp.formTitle || sp.name} desc={sp.formDesc || ''}>
                {renderSportForm()}
            </FormCard>

            <FormCard title="회고" desc={isCombat ? '컨디션, 운동강도, 오늘 배운 점을 남겨 다음 훈련에 이어가요.' : '컨디션, 운동강도, 오늘의 느낌을 남겨 다음 기록과 비교해요.'}>
                <div className="form-group">
                    <label className="form-label">컨디션</label>
                    <div className="score-row">
                        <CondPips value={form.condScore} onChange={v => set('condScore', v)} />
                        <span className="score-val">{form.condScore}/5</span>
                    </div>
                </div>
                <div className="form-group">
                    <label className="form-label">운동강도</label>
                    <div className="score-row">
                        <CondPips value={form.fatScore} onChange={v => set('fatScore', v)} filled={false} />
                        <span className="score-val muted">{form.fatScore}/5</span>
                    </div>
                </div>
                <div className="form-group">
                    <label className="form-label">오늘의 일기 · 배운점</label>
                    <textarea className="form-input" rows={4} placeholder="오늘 느낀 점, 개선할 점, 다음에 이어갈 포인트..."
                        value={diary} onChange={e => setDiary(e.target.value)} />
                </div>
            </FormCard>

            <FormCard title="소셜 공유" desc="운동 기록을 피드에 공개할지 정하고, 함께 남길 말을 적어보세요.">
                <div className="form-group">
                    <label className="form-label">공개 범위</label>
                    <div className="visibility-card-grid">
                        {[
                            { key: 'public', title: '전체 공개', desc: '모든 사용자 피드에 표시' },
                            { key: 'friends', title: '친구 공개', desc: '나와 친구 피드에 표시' },
                            { key: 'private', title: '비공개', desc: '내 기록에만 저장' },
                        ].map(option => (
                            <button key={option.key}
                                className={`visibility-card ${form.visibility === option.key ? 'active' : ''}`}
                                onClick={() => { set('visibility', option.key); localStorage.setItem('wj_visibility', option.key); }}>
                                <strong>{option.title}</strong>
                                <span>{option.desc}</span>
                            </button>
                        ))}
                    </div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">소셜에 남길 말</label>
                    <textarea className="form-input" rows={3}
                        placeholder={form.visibility === 'private' ? '비공개 기록에는 피드 메시지가 표시되지 않아요' : '예: 오늘은 체력운동을 위주로 했다.'}
                        value={form.postMessage || ''}
                        onChange={e => set('postMessage', e.target.value)}
                        disabled={form.visibility === 'private'} />
                </div>
            </FormCard>

            <button className="save-btn" onClick={() => { if (validate()) onSave(getFormData(), isCardio ? cardioValues : null); }}>
                {isEdit ? '수정 저장' : '기록 저장하기'}
            </button>
            {!isEdit && (
                <div style={{ marginTop: 8 }}>
                    {!showTplInput ? (
                        <button className="save-btn secondary" onClick={() => setShowTplInput(true)}>루틴으로도 저장하기</button>
                    ) : (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input className="form-input" placeholder="루틴 이름 (예: 월요일 등 운동)"
                                value={tplName} onChange={e => setTplName(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && tplName.trim() && validate()) onSave(getFormData(), isCardio ? cardioValues : null, tplName.trim()); }}
                                autoFocus />
                            <button className="tpl-btn primary" style={{ whiteSpace: 'nowrap', padding: '10px 14px', flexShrink: 0 }}
                                onClick={() => { if (tplName.trim() && validate()) onSave(getFormData(), isCardio ? cardioValues : null, tplName.trim()); }}>저장</button>
                        </div>
                    )}
                </div>
            )}

            {showBuddyPicker && (
                <div className="confirm-backdrop">
                    <div className="buddy-panel">
                        <div className="buddy-panel-head">
                            <div>
                                <div className="confirm-title">함께 운동한 친구</div>
                                <div className="confirm-desc">자주 태그한 친구가 먼저 보여요.</div>
                            </div>
                            <button className="remove-btn" onClick={() => setShowBuddyPicker(false)}>×</button>
                        </div>
                        <input className="form-input" placeholder="친구 이름 또는 ID 검색"
                            value={buddyQuery} onChange={e => setBuddyQuery(e.target.value)} autoFocus />
                        <div className="buddy-list">
                            {sortedFriends.length === 0 ? (
                                <div className="empty-text" style={{ padding: '18px 0' }}>검색 결과가 없어요</div>
                            ) : sortedFriends.map(friend => {
                                const active = (form.buddies || []).some(b => b.uid === friend.uid);
                                return (
                                    <label key={friend.uid} className="buddy-row">
                                        <input type="checkbox" checked={active} onChange={() => toggleBuddy(friend)} />
                                        <span className="friend-avatar">{friend.nickname?.slice(0, 1) || '?'}</span>
                                        <span className="buddy-row-main">
                                            <strong>{friend.nickname}</strong>
                                            <em>@{friend.userId}{buddyCounts[friend.uid] ? ` · 함께 ${buddyCounts[friend.uid]}회` : ''}</em>
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                        <button className="save-btn" onClick={() => setShowBuddyPicker(false)}>선택 완료</button>
                    </div>
                </div>
            )}
            <div style={{ height: 8 }} />
        </>
    );
}
